import {
  LoadingState,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';

import { fetchUnitLeadHistory } from '../../../../api/lead.js';

import type { LeadHistoryEntry } from '../../../../api/lead.js';


interface Props {
  unitId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Side drawer that lists every change to `unit_lead_status` for
 *  this unit, newest first. The diff renderer uses a "before → after"
 *  format with the *current* row used as the "after" anchor for the
 *  most recent entry, and the next entry's prior values used as the
 *  "after" anchor for the one below it. We rely on the API ordering
 *  the rows desc by `changed_at`. */
export function HistoryDrawer({ unitId, open, onOpenChange }: Props) {
  const query = useQuery({
    queryKey: ['unit-lead-history', unitId],
    queryFn: () => fetchUnitLeadHistory(unitId),
    enabled: open,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg">
        <SheetHeader>
          <SheetTitle>Lead change history</SheetTitle>
          <SheetDescription>
            Every change to this unit's lead status, newest first.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 overflow-y-auto pr-1">
          {query.isLoading ? (
            <LoadingState rows={4} />
          ) : (query.data?.entries.length ?? 0) === 0 ? (
            <p className="rounded-md border border-dashed border-border bg-surface-muted/40 p-4 text-sm text-fg-muted">
              No changes recorded yet.
            </p>
          ) : (
            <ol className="space-y-3">
              {query.data!.entries.map((entry, idx) => (
                <li
                  key={entry.id}
                  className="rounded-md border border-border bg-surface p-3 text-sm"
                >
                  <HistoryRow
                    entry={entry}
                    nextEntry={query.data!.entries[idx - 1] ?? null}
                  />
                </li>
              ))}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function HistoryRow({
  entry,
  nextEntry,
}: {
  entry: LeadHistoryEntry;
  /** The *newer* entry in the list (or null for the most recent
   *  one). We use its `prior` snapshot as our "after" snapshot for
   *  the diff renderer, since the API doesn't persist a full "after"
   *  on each row. For the most recent entry there's no newer one;
   *  the diff just shows "→ updated". */
  nextEntry: LeadHistoryEntry | null;
}) {
  const actor = entry.changedByName ?? entry.changedByEmail ?? 'Unknown user';
  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-xs text-fg-muted">
        <span>{new Date(entry.changedAt).toLocaleString()}</span>
        <span>{actor}</span>
      </div>
      <ul className="mt-2 space-y-1 text-sm">
        {entry.changedFields.map((field) => (
          <li key={field} className="flex items-baseline gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {prettyField(field)}
            </span>
            <span className="font-mono text-xs text-fg-secondary">
              {formatPrior(entry, field)} → {formatNext(entry, nextEntry, field)}
            </span>
          </li>
        ))}
      </ul>
      {entry.changeNote ? (
        <p className="mt-2 rounded bg-surface-muted px-2 py-1 text-xs italic text-fg-secondary">
          “{entry.changeNote}”
        </p>
      ) : null}
    </div>
  );
}

function prettyField(field: string): string {
  switch (field) {
    case 'friction_status':
      return 'Friction';
    case 'exemption_status':
      return 'Exemption';
    case 'exemption_letter_document_id':
      return 'Exemption letter';
    case 'test_result':
      return 'Test result';
    case 'positive_count':
      return 'Positive count';
    case 'latest_xrf_date':
      return 'XRF date';
    case 'latest_xrf_report_document_id':
      return 'XRF report';
    case 'notes':
      return 'Notes';
    default:
      return field;
  }
}

function formatPrior(entry: LeadHistoryEntry, field: string): string {
  const { prior } = entry;
  switch (field) {
    case 'friction_status':
      return prior.frictionStatus ?? '—';
    case 'exemption_status':
      return prior.exemptionStatus ?? '—';
    case 'exemption_letter_document_id':
      return prior.exemptionLetterDocumentId ? 'attached' : 'none';
    case 'test_result':
      return prior.testResult ?? '—';
    case 'positive_count':
      return prior.positiveCount != null ? String(prior.positiveCount) : '—';
    case 'latest_xrf_date':
      return prior.latestXrfDate ?? '—';
    case 'latest_xrf_report_document_id':
      return prior.latestXrfReportDocumentId ? 'attached' : 'none';
    case 'notes':
      return prior.notes ? truncate(prior.notes, 40) : '—';
    default:
      return '—';
  }
}

function formatNext(
  _entry: LeadHistoryEntry,
  nextEntry: LeadHistoryEntry | null,
  field: string,
): string {
  if (!nextEntry) return 'current';
  return formatPrior(nextEntry, field);
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
